import React, { useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import AsyncSelect from 'react-select/async';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444', dangerDark: '#dc2626',
};

const inp  = { border: `1px solid ${C.cardBorder}`, borderRadius: '7px', fontSize: '12px', height: '28px', width: '100%', padding: '0 8px', outline: 'none', backgroundColor: '#f8fafa', boxSizing: 'border-box', fontFamily: 'inherit' };
const inpR = { ...inp, textAlign: 'right' };
const card = { backgroundColor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, marginBottom: '10px', overflow: 'hidden' };
const cardHead = (color = C.accent) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderBottom: `1px solid ${C.cardBorder}`, backgroundColor: `${color}18`, fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.5px' });
const cardBody = { padding: '10px 12px' };
const lbl = { fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };

const selectStyles = {
  control: (base) => ({ ...base, minHeight: '28px', height: '28px', fontSize: '12px', borderRadius: '7px', borderColor: C.cardBorder }),
  valueContainer: (base) => ({ ...base, height: '28px', padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, height: '28px' }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  menu: (base) => ({ ...base, fontSize: '12px' }),
};

const JournalEntryEdit = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isAuthenticated, companyId, branchId, userId, permissions, token, logout } = useAuth();

  const [isLoading, setIsLoading]               = useState(false);
  const [errorMessage, setErrorMessage]         = useState('');
  const [successMessage, setSuccessMessage]     = useState('');
  const [supplierOptions, setSupplierOptions]   = useState([]);
  const [selectedOption, setSelectedOption]     = useState(null);
  const [selectedChildOption, setSelectedChildOption] = useState(null);
  const [itemsList, setItemsList]               = useState([]);

  const parseAndFix = (val) => parseFloat(val) || 0;

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const [formData, setFormData] = useState({
    CompanyId: companyId,
    BillDate: '',
    BillNo: '',
    FinYear: '',
    BranchId: branchId,
    MasterId: '',
    Amount: 0,
    CAmount: 0,
    Remarks: '',
    IsType: true,
    IsIGST: false,
    GSTRate: 0,
    CGSTAmount: '0.00',
    SGSTAmount: '0.00',
    IGSTAmount: '0.00',
    RoundOff: '0.00',
    TotalAmount: '0.00',
    createdBy: userId,
  });

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchData('JournelEntryAPI', `/${id}`, token)
      .then(res => {
        if (res.responseCode === '1111') {
          setFormData(res.response_data.JournalEntry);
          setItemsList(res.response_data.JournalEntryDetails);
          setSelectedOption({
            value: res.response_data?.JournalEntry?.MasterId ?? 1,
            label: res.response_data?.JournalEntry?.MasterName ?? '',
          });
        }
      })
      .catch(err => console.error('Error fetching journal entry:', err));
  }, [id]);

  const fetchOptions = async (inputValue) => {
    if (inputValue.length <= 1) return [];
    try {
      const res = await fetchData('JournelEntryAPI', `/Master/${companyId}/${inputValue}`, token);
      if (res.responseCode === '1111' && Array.isArray(res.response_data)) {
        setSupplierOptions(res.response_data);
        return res.response_data.map(item => ({ value: item.Id, label: item.Name }));
      }
    } catch (err) { console.error('Error fetching options:', err); }
    return [];
  };

  const handleCustomerSelect = (selected) => {
    setSelectedOption(selected);
    setFormData(prev => ({ ...prev, MasterId: selected ? selected.value : '' }));
  };

  const handleChildSelect = (selected) => {
    setSelectedChildOption(selected);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updated = name === 'IsType' ? value === '1' : type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: updated }));
  };

  // Recalculate totals whenever itemsList, GSTRate or IsIGST changes
  useEffect(() => {
    const amt = itemsList.reduce((sum, item) => sum + parseAndFix(item.Amount), 0);
    const GSTRate = parseAndFix(formData.GSTRate);
    let cgst = 0, sgst = 0, igst = 0;
    if (formData.IsIGST) {
      igst = ((amt * GSTRate) / 100).toFixed(2);
    } else {
      cgst = ((amt * (GSTRate / 2)) / 100).toFixed(2);
      sgst = ((amt * (GSTRate / 2)) / 100).toFixed(2);
    }
    const net = parseAndFix(amt) + parseAndFix(igst) + parseAndFix(cgst) + parseAndFix(sgst);
    const roundOff = Math.round(net) - net;
    const grandTotal = net + roundOff;
    setFormData(prev => ({
      ...prev,
      Amount: amt,
      CGSTAmount: parseFloat(cgst).toFixed(2),
      SGSTAmount: parseFloat(sgst).toFixed(2),
      IGSTAmount: parseFloat(igst).toFixed(2),
      RoundOff: parseFloat(roundOff).toFixed(2),
      TotalAmount: parseFloat(grandTotal).toFixed(2),
    }));
  }, [itemsList, formData.GSTRate, formData.IsIGST]);

  const addItemToList = () => {
    if (!selectedChildOption || !formData.CAmount || parseAndFix(formData.CAmount) <= 0) {
      setErrorMessage('Please select a master and enter a valid amount!');
      return;
    }
    setErrorMessage('');
    const newItem = {
      slNo: itemsList.length + 1,
      MasterName: selectedChildOption.label,
      MasterId: selectedChildOption.value,
      Amount: formData.CAmount,
    };
    setItemsList(prev => [...prev, newItem]);
    setSelectedChildOption(null);
    setFormData(prev => ({ ...prev, CAmount: 0 }));
  };

  const handleCalculation = (e, index) => {
    const { name, value } = e.target;
    setItemsList(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  const handleDeleteItem = (index) => {
    setItemsList(prev => prev.filter((_, i) => i !== index));
  };

  const submitData = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (!formData.Amount || formData.Amount <= 0) { setErrorMessage('Amount must be greater than 0!'); setIsLoading(false); return; }
    if (itemsList.length === 0) { setErrorMessage('Please add at least one item!'); setIsLoading(false); return; }

    const payload = {
      JournalEntry: formData,
      JournalEntryDetails: itemsList,
    };

    try {
      await updateData('JournelEntryAPI', `${id}`, payload, token);
      setSuccessMessage('Journal Entry Updated Successfully!');
      setIsLoading(false);
      history.push('/JournelEntryIndex');
    } catch (err) {
      console.error('Error updating journal entry:', err);
      setErrorMessage('Failed to update Journal Entry. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: C.bg }}>

      {/* ── Header ── */}
      <div style={{ ...card, overflow: 'visible' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AccountBalanceWalletIcon style={{ fontSize: 16, color: C.accent }} />
            <span style={{ fontWeight: 800, fontSize: '13px', color: C.text, fontFamily: 'Inter, sans-serif' }}>Edit Journal Entry</span>
          </div>

          {/* Debit / Credit toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: '#1d4ed8', cursor: 'pointer' }}>
              <input type="radio" name="IsType" value="1" checked={formData.IsType === true} onChange={handleChange} />
              Debit
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: '#b45309', cursor: 'pointer' }}>
              <input type="radio" name="IsType" value="0" checked={formData.IsType === false} onChange={handleChange} />
              Credit
            </label>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={submitData} disabled={isLoading}
              style={{ height: '28px', padding: '0 16px', borderRadius: '7px', border: 'none', backgroundColor: C.accent, color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'inherit' }}>
              {isLoading ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : '✓ Update'}
            </button>
            <Link to={{ pathname: '/JournelEntryIndex', state: { from: '/JournalEntryEdit' } }}
              onClick={() => localStorage.setItem('shouldReload', 'true')}
              style={{ height: '28px', padding: '0 14px', borderRadius: '7px', border: `1px solid ${C.cardBorder}`, backgroundColor: C.card, color: C.muted, fontWeight: 600, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              ← Back
            </Link>
          </div>
        </div>
      </div>

      {/* ── Error / Success ── */}
      {errorMessage && (
        <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px', fontSize: '11px', color: C.danger }}>
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '7px', fontSize: '11px', color: '#16a34a' }}>
          {successMessage}
        </div>
      )}

      {/* ── Bill Details ── */}
      <div style={card}>
        <div style={cardHead()}>📋 Entry Details</div>
        <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
          <div>
            <label style={lbl}>Master <span style={{ color: C.danger }}>*</span></label>
            <AsyncSelect cacheOptions loadOptions={fetchOptions} onChange={handleCustomerSelect} value={selectedOption}
              placeholder="Type to search…" menuPortalTarget={document.body} styles={selectStyles} />
          </div>
          <div>
            <label style={lbl}>Date <span style={{ color: C.danger }}>*</span></label>
            <input type="date" name="BillDate" value={formatDateForInput(formData.BillDate)} onChange={handleChange} style={inp} />
          </div>
          <div>
            <label style={lbl}>Number</label>
            <input type="text" value={`${formData.BillNo || ''}/${formData.FinYear || ''}`} readOnly style={{ ...inp, backgroundColor: '#f0f4f4', cursor: 'default' }} />
          </div>
          <div>
            <label style={lbl}>Remarks</label>
            <input type="text" name="Remarks" value={formData.Remarks || ''} onChange={handleChange} placeholder="Remarks" style={inp} />
          </div>
          <div>
            <label style={lbl}>Amount (auto)</label>
            <input type="number" value={formData.Amount} readOnly style={{ ...inpR, backgroundColor: '#f0f4f4', cursor: 'default', fontWeight: 700, color: '#0D47A1' }} />
          </div>
          <div>
            <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
              GST %
              <input type="checkbox" name="IsIGST" checked={formData.IsIGST || false} onChange={handleChange} />
              <span style={{ color: C.danger, fontSize: '9px' }}>IGST</span>
            </label>
            <select name="GSTRate" value={formData.GSTRate || 0} onChange={handleChange}
              style={{ ...inp, cursor: 'pointer' }}>
              {[0, 3, 5, 12, 18, 28].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>

        {/* GST summary bar */}
        <div style={{ display: 'flex', gap: '20px', padding: '6px 12px', borderTop: `1px solid ${C.cardBorder}`, backgroundColor: '#f8fafa', flexWrap: 'wrap' }}>
          {[['CGST', formData.CGSTAmount], ['SGST', formData.SGSTAmount], ['IGST', formData.IGSTAmount], ['Round Off', formData.RoundOff], ['Total', formData.TotalAmount]].map(([label, val]) => (
            <div key={label} style={{ fontSize: '11px', color: C.muted }}>
              {label}: <strong style={{ color: '#B71C1C' }}>{val}</strong>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '10px' }}>

        {/* ── Items table ── */}
        <div style={card}>
          <div style={cardHead('#1F2A38')}>📒 Selected Master List ({itemsList.length})</div>
          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead style={{ backgroundColor: '#1F2A38', color: '#fff', position: 'sticky', top: 0 }}>
                <tr>
                  {['#', 'Party', 'Amount', ''].map((h, i) => (
                    <th key={i} style={{ padding: '6px 8px', textAlign: i === 2 ? 'right' : i === 3 ? 'right' : 'left', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {itemsList.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: C.muted, fontSize: '12px' }}>No items added yet</td></tr>
                ) : itemsList.map((item, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: `1px solid ${C.cardBorder}` }}>
                    <td style={{ padding: '3px 8px', color: C.muted, fontSize: '11px' }}>{index + 1}</td>
                    <td style={{ padding: '3px 8px', fontWeight: 600 }}>{item.MasterName}</td>
                    <td style={{ padding: '2px 4px', width: '110px' }}>
                      <input type="number" value={item.Amount} name="Amount"
                        onChange={e => handleCalculation(e, index)}
                        style={{ ...inpR, height: '24px', borderRadius: '4px', backgroundColor: '#fffbeb' }} />
                    </td>
                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>
                      <button onClick={() => handleDeleteItem(index)}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: C.danger, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        <DeleteIcon style={{ fontSize: 15 }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Add Master panel ── */}
        <div style={card}>
          <div style={cardHead('#f59e0b')}>➕ Add Master</div>
          <div style={cardBody}>
            <div style={{ marginBottom: '10px' }}>
              <label style={lbl}>Master <span style={{ color: C.danger }}>*</span></label>
              <AsyncSelect cacheOptions loadOptions={fetchOptions} onChange={handleChildSelect} value={selectedChildOption}
                placeholder="Type to search…" menuPortalTarget={document.body} styles={selectStyles} />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={lbl}>Amount</label>
              <input type="number" name="CAmount" value={formData.CAmount} onChange={handleChange}
                placeholder="Enter amount" min={0} step="0.01" style={inpR} />
            </div>
            <button onClick={addItemToList}
              style={{ width: '100%', height: '30px', borderRadius: '7px', border: 'none', backgroundColor: C.accent, color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}>
              + Add to List
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default JournalEntryEdit;
